import { UserDetailResponse } from "@/web-client/api";
import useCleanEditableText from "@/hooks/useCleanEditableText";
import Link from "next/link";
import { FC, useCallback, useContext } from "react";
import SocialLinks from "@/components/SocialLinks";
import UserIcon, { UserIconLayout } from "@/features/user/components/UserIcon";
import useAnalytics from "@/hooks/useAnalytics";
import useTeamsByUserId from "@/features/team/hooks/useTeamsByUserId";
import ValueItem from "@/components/ValueItem";
import FollowBtn from "@/features/user/components/FollowBtn";
import useDevice from "@/hooks/useDevice";
import Avatar, { AvatarSize } from "@/components/Avatar";
import useMyself from "@/features/user/hooks/useMyself";
import IconSettings from "@/assets/imgs/svg/icon-settings.svg";
import useFollow from "@/hooks/useFollow";
import StateContext from "@/contexts/StateContext";
import { theme } from "tailwind.config";

interface Props {
  user: UserDetailResponse;
}

const UserProfile: FC<Props> = ({ user }): JSX.Element => {
  const { update: mutateUser } = useContext(StateContext);
  const { followHandler } = useFollow(user, (updated) =>
    mutateUser(updated, false),
  );
  const { isPc, isSp } = useDevice();
  const { event } = useAnalytics();
  const clickHandler = useCallback(
    (linkType: string) => {
      event("click_button", {
        content_type: linkType,
        item_id: `U_${user.id}`,
      });
    },
    [user, event],
  );

  const cleanedProfile = useCleanEditableText(user.profile);

  const { teams } = useTeamsByUserId(user.id);
  const isMyself = useMyself(user.id);

  return (
    <div className="flex flex-col items-center max-w-[800px]">
      <div className="flex laptop:flex-start flex-col laptop:flex-row items-center laptop:items-start space-y-24 laptop:space-y-0 laptop:space-x-48">
        <div className="flex flex-col gap-24">
          <div className="flex flex-col items-center space-y-12">
            <UserIcon
              user={user}
              hasName={false}
              size={AvatarSize.Huge}
              layout={UserIconLayout.Vertical}
            />
            {isSp && (
              <>
                <div className="flex items-center gap-8">
                  <h1 className="font-bold text-xl text-center">
                    {user.screen_name}
                  </h1>
                  {isMyself && (
                    <Link href="/settings/profile" prefetch={false}>
                      <button className="bg-gray-200 rounded-full p-8">
                        <IconSettings
                          idth={16}
                          height={16}
                          fill={theme.colors.gray[950]}
                        />
                      </button>
                    </Link>
                  )}
                </div>

                {teams && teams.length > 0 && (
                  <div className="flex items-center gap-8">
                    <span className="font-bold text-secondary text-xs flex-shrink-0">
                      on Teams:
                    </span>
                    <div className="[&>*]:float-left [&>*+*]:ml-4 [&>*+*]:mb-4">
                      {teams.map((team) => (
                        <Link
                          key={team.id}
                          href={`/teams/${team.id}/projects`}
                          prefetch={false}
                        >
                          <Avatar
                            alt={`${team.name}のロゴ`}
                            size={AvatarSize.Small}
                            src={
                              team.logo
                                ? team.logo.urls.small
                                : "/imgs/default_team_icon.png"
                            }
                          />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col items-center gap-16">
            <FollowBtn
              userId={user.id}
              onClick={followHandler}
              isFollowing={user.is_following}
            />

            <div className="flex [&>*]:px-16 [&>*+*]:border-l [&>*+*]:border-primary">
              <ValueItem
                value={user.following_count}
                href={`/users/${user.id}/following`}
                label={"フォロー中"}
              />
              <ValueItem
                value={user.followers_count}
                href={`/users/${user.id}/followers`}
                label={"フォロワー"}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col space-y-16">
          {isPc && (
            <div className="space-y-8">
              <div className="flex flex-col items-center laptop:flex-row">
                <h1 className="text-2xl font-bold">{user.screen_name}</h1>
                <div className="">
                  {isMyself && (
                    <Link href="/settings/profile" prefetch={false}>
                      <button className="rounded-full p-6 hover:bg-gray-200 hover:text-white">
                        <IconSettings idth={20} height={20} fill={"#999"} />
                      </button>
                    </Link>
                  )}
                </div>
              </div>

              {teams && teams.length > 0 && (
                <div className="flex items-center gap-8">
                  <span className="font-bold text-secondary text-xs">
                    on Teams:
                  </span>
                  <div className="flex gap-4">
                    {teams.map((team) => (
                      <Link key={team.id} href={`/teams/${team.id}/projects`}>
                        <Avatar
                          alt={`${team.name}のロゴ`}
                          size={AvatarSize.Small}
                          src={
                            team.logo
                              ? team.logo.urls.small
                              : "/imgs/default_team_icon.png"
                          }
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {user.profile && (
            <div className="max-w-[600px]">
              <p className="text-xs leading-loose whitespace-pre-wrap">
                {cleanedProfile}
              </p>
            </div>
          )}

          {(user.links || user.contact_email) && (
            <div className="flex items-center laptop:justify-start justify-center space-x-12">
              <div className="small:mx-auto">
                <SocialLinks
                  contactableResponese={user}
                  clickHandler={clickHandler}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
